import { cache } from '../../cache-impl/cache-registry.js';
import { ClientApi } from '../../../api/client-api.js';
import { createNewQuote } from '../../quotes/quote-creator.js';
import { customElement } from 'lit/decorators.js';
import { DataCacheGeneric } from '../../../../webmodule-common/cache/generic-data-cache.js';
import { DevelopmentError } from '../../../../webmodule-common/other/development-error.js';
import { displaySupplierTACNotApprovedMsg } from '../../../v6config/supplier-services.js';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid.js';
import { fireQuickErrorToast, fireQuickSuccessToast } from '../../../../webmodule-common/other/toast-away.js';
import { FranchiseeProjectDetailView } from './franchisee-project-detail-view.js';
import { FranchiseeProjectDocumentView } from './franchisee-project-document-view.js';
import { FranchiseeProjectResourceView } from './franchisee-project-resource-view.js';
import {
  FranchiseeQuoteContainerManager,
  mapPaymentProfileCalculation
} from '../../quotes/data/franchisee-quote-manager.js';
import { getApiFactory } from '../../../api/api-injector.js';
import { getProjectStateChangeReasonsForState } from '../../cache-impl/project-state-change-reason-cache.js';
import { getQuoteContainerManager } from '../../quotes/quote-ui-adapter.js';
import { getSupplier } from '../../quotes/supplier-selector.js';
import { isEmptyOrSpace } from '../../../../webmodule-common/other/ui/string-helper-functions.js';
import { launchQuote } from '../../../quotes/ui/launcher.js';
import { PaymentProfileCacheData } from '../../cache-impl/cache-data.js';
import { ProjectDataEntryView, ProjectView, ProjectViewOptions } from '../../../projects/views/project-view.js';
import { ProjectDocumentView } from '../../../projects/views/project-document-view.js';
import { ProjectResourceView } from '../../../projects/views/project-resource-view.js';
import {
  ProjectState,
  ProjectStateChangeReason,
  Resource,
  ResourceType
} from '../../../api/dealer-api-interface-project.js';
import { PurchaseOrderState } from '../../../api/dealer-api-interface-franchisee.js';
import { QuoteState, QuoteType } from '../../../api/dealer-api-interface-quote.js';
import { showValidations } from '../../../../webmodule-common/other/ui/modal-validationhandler.js';
import { tlang } from '../../../../webmodule-common/other/language/lang.js';
import { userDataStore } from '../../common/current-user-data-store.js';
import type { NullPromise } from '../../../../webmodule-common/interop/webmodule-interop.js';

@customElement('wm-franchiseeprojectdeview')
export class FranchiseeProjectDEView extends ProjectDataEntryView {
  clientApi: ClientApi = getApiFactory().client();
  private projectCache = cache().project;
  private clientCache = cache().client;
  private clientTypeCache = cache().paymentProfile;
  private contactCache = cache().contact;

  protected projectDetailViewFactory(_options: ProjectViewOptions) {
    return new FranchiseeProjectDetailView({
      projectManager: this.projectContainerManager
    });
  }

  protected projectResourceViewFactory(): ProjectResourceView {
    return new FranchiseeProjectResourceView({
      projectManager: this.projectContainerManager,
      quoteCache: cache().quote,
      purchaseOrderCache: cache().purchaseOrder,
      canClose: async () => {
        if (await this.canClose()) {
          await this.tryClose();
          return true;
        }
        return false;
      }
    });
  }

  protected projectDocumentsViewFactory(): ProjectDocumentView {
    return new FranchiseeProjectDocumentView({
      projectManager: this.projectContainerManager,
      canClose: async () => {
        if (await this.canClose()) {
          await this.tryClose();
          return true;
        }
        return false;
      }
    });
  }

  protected async internalCreateQuote(): Promise<boolean> {
    //REFACTOR - remove business logic from UI, add to container manager and parameterize
    //ie qcm.createProjectQuote()
    const supplier = await getSupplier();
    if (isEmptyOrSpace(supplier.supplierId)) return false;

    if (!userDataStore.supplierTACApproved(supplier.supplierId)) {
      await displaySupplierTACNotApprovedMsg(supplier, true);
      return false;
    }
    const quoteContainer = await createNewQuote({
      supplierId: supplier.supplierId,
      owner: userDataStore.defaultBranch.id
    });
    if (quoteContainer) {
      const quoteManager = getQuoteContainerManager(quoteContainer) as FranchiseeQuoteContainerManager;
      quoteManager.quote.state = QuoteState.Active;
      quoteManager.generateQuoteNumberOnSave();

      const branchQuote = quoteManager.branchQuote;
      branchQuote.clientId = this.projectContainerManager.project.clientId;
      branchQuote.clientName = this.clientCache.getLocal(branchQuote.clientId)?.displayValue ?? '';
      branchQuote.clientTypeId = this.projectContainerManager.project.clientTypeId;
      branchQuote.clientTypeName = this.clientTypeCache.getLocal(branchQuote.clientTypeId)?.displayValue ?? '';
      branchQuote.contactId = this.projectContainerManager.project.contactId;
      branchQuote.contactName = this.contactCache.getLocal(branchQuote.contactId)?.displayValue ?? '';

      const paymentProfile = await cache().paymentProfile.get(branchQuote.clientTypeId);
      if (paymentProfile && paymentProfile.data) {
        const profile = paymentProfile.data as PaymentProfileCacheData;
        //Update Terms
        if (quoteManager.quote.quoteType === QuoteType.Quote)
          quoteManager.quote.termsAndConditions = profile.paymentProfile.quoteTerms;
        else quoteManager.quote.termsAndConditions = profile.paymentProfile.estimateTerms;

        quoteManager.quotePrice.percentMarginOrMarkup = profile.paymentProfile.value;
        quoteManager.quotePrice.calculation = mapPaymentProfileCalculation(profile.paymentProfile.calculation);
      }

      const updatedQuote = await quoteManager.saveQuote(true);
      if (updatedQuote) {
        const resource = await this.projectApi.createProjectResourceReference({
          resourceId: quoteContainer.quoteId,
          projectId: this.projectContainerManager.projectId,
          typeOf: ResourceType.Quote,
          parentResourceId: emptyGuid,
          parentTypeOf: ResourceType.None
        });
        if (resource) {
          await Promise.all([
            this.projectCache.updateLocal(this.projectContainerManager.projectId),
            cache().projectResourceLink.updateLocal(this.projectContainerManager.projectId),
            cache().projectResourceLink.updateLocal(quoteContainer.quoteId)
          ]);
          fireQuickSuccessToast(tlang`Created %%quote%% for ${this.projectContainerManager.projectTitle}`);
          return launchQuote(quoteContainer.quoteId);
        }
      }
      // if we get here A) The quote update has failed and B) the project-quote link wasn't created
      fireQuickErrorToast(tlang`Unable to create %%quote%% for ${this.projectContainerManager.projectTitle}`);
      // if we were unable to create the project link, remove the alternative quote
      return await quoteManager.deleteQuote();
    }
    fireQuickErrorToast(tlang`Unable to create %%quote%%`);
    return false;
  }

  protected async canCreateQuote(): Promise<boolean> {
    const project = this.projectContainer.project;
    if (!project) {
      throw new DevelopmentError(tlang`%%project%% container is empty`);
    }
    const isProjectActive = project.state !== ProjectState.Cancelled && project.state !== ProjectState.Completed;
    if (!isProjectActive) return false;
    const errors = this.getValidationErrors();

    if (errors.length !== 0) {
      await showValidations(errors);
      return false;
    }

    return true;
  }

  protected async setProjectState(projectState: ProjectState): Promise<boolean> {
    // force a reload of the project to get updated quote/purchase order resources
    this.projectContainer.project = null;
    await this.projectContainerManager.needsProject();

    const errors: string[] = [];
    const stateText = projectState === ProjectState.Cancelled ? 'cancel' : 'complete';
    const quoteResources = (await this.internalLoadResources(ResourceType.Quote, this.quoteCache)) ?? [];
    const orderResources =
      (await this.internalLoadResources(ResourceType.PurchaseOrder, this.purchaseOrderCache)) ?? [];

    if (projectState === ProjectState.Cancelled) {
      errors.push(...this.internalValidateProjectCancel(quoteResources, orderResources));
    } else if (projectState === ProjectState.Completed) {
      errors.push(...this.internalValidateProjectComplete(quoteResources, orderResources));
    }

    if (errors.length > 0) {
      await showValidations(
        errors,
        () => tlang`Unable to ${stateText} %%project%% as one or more items have not been completed.`
      );
      return false;
    } else {
      return super.setProjectState(projectState);
    }
  }

  protected async internalLoadResources(
    resourceType: ResourceType,
    objectCache: DataCacheGeneric
  ): Promise<Resource[] | undefined> {
    const items = this.projectContainer.resources?.filter(x => x.typeOf === resourceType);
    if (items) {
      const keys = items.map(x => x.resourceId ?? emptyGuid) ?? [];

      //if there are any other callbacks we need to do, add them to the
      //all array which will let them run at the same time
      await Promise.all([objectCache.preFetch(keys)]);
    }
    return items;
  }

  protected internalValidateProjectCancel(quoteResources: Resource[], orderResources: Resource[]): string[] {
    const errors: string[] = [];

    /*
         Cancel Requirements
         - Only Accepted, Rejected, Cancelled and Lapsed quotes
         - Only Cancelled purchase orders
         */
    let hasAccepted = false;
    quoteResources.forEach(resource => {
      const quoteSummary = this.quoteCache.getLocalData(resource.resourceId)?.quoteSummary;
      if (quoteSummary) {
        if (this.isQuoteStateOutstanding(quoteSummary.state)) {
          errors.push(
            tlang`%%quote%% '${quoteSummary.title}' is still outstanding (Currently ${QuoteState[quoteSummary.state]}).`
          );
        } else if (quoteSummary.state === QuoteState.Approved) {
          //TODO# we may test for more than the accepted state with new supplier workflow
          hasAccepted = true;
        }
      }
    });
    // probably should never reach here, but just in case...
    if (hasAccepted) {
      errors.push(tlang`Cannot cancel a %%project%% with accepted %%quote%%s`);
    }

    orderResources.forEach(resource => {
      const orderSummary = this.purchaseOrderCache.getLocalData(resource.resourceId)?.purchaseOrder;
      if (
        orderSummary &&
        orderSummary.state !== PurchaseOrderState.Completed &&
        orderSummary.state !== PurchaseOrderState.Cancelled
      ) {
        errors.push(
          tlang`%%purchase-order%% '${orderSummary.title}' has not been completed (Currently ${
            PurchaseOrderState[orderSummary.state]
          }).`
        );
      }
    });

    return errors;
  }

  protected internalValidateProjectComplete(quoteResources: Resource[], orderResources: Resource[]): string[] {
    const errors: string[] = [];
    /*
         Complete Requirements
         - Only Accepted, Rejected, Cancelled and Lapsed quotes
         - Only Completed and Cancelled purchase orders
         */
    quoteResources.forEach(resource => {
      const quoteSummary = this.quoteCache.getLocalData(resource.resourceId)?.quoteSummary;
      if (quoteSummary && this.isQuoteStateOutstanding(quoteSummary.state)) {
        errors.push(
          tlang`%%quote%% '${quoteSummary.title}' has not been completed (Currently ${QuoteState[quoteSummary.state]}).`
        );
      }
    });

    orderResources.forEach(resource => {
      const orderSummary = this.purchaseOrderCache.getLocalData(resource.resourceId)?.purchaseOrder;
      if (
        orderSummary &&
        orderSummary.state !== PurchaseOrderState.Completed &&
        orderSummary.state !== PurchaseOrderState.Cancelled
      ) {
        errors.push(
          tlang`%%purchase-order%% '${orderSummary.title}' has not been completed (Currently ${
            PurchaseOrderState[orderSummary.state]
          }).`
        );
      }
    });

    return errors;
  }

  protected isQuoteStateOutstanding(quoteState: QuoteState): boolean {
    return (
      quoteState !== QuoteState.Approved &&
      quoteState !== QuoteState.Cancelled &&
      quoteState !== QuoteState.Rejected &&
      quoteState !== QuoteState.Lapsed &&
      quoteState !== QuoteState.Accepted_RefusedBySupplier
    );
  }

  protected async getProjectReasons(projectState: ProjectState): NullPromise<ProjectStateChangeReason[]> {
    const franchiseeId = userDataStore.franchisee.id;
    const stateReasons = await getProjectStateChangeReasonsForState(projectState, franchiseeId);
    if (stateReasons) {
      const displayOrder = stateReasons.presentation.itemDisplayOrder;
      const sortedItems: ProjectStateChangeReason[] = [];
      displayOrder.forEach(elementId => {
        const item = stateReasons.stateChangeReasons.find(item => item.id === elementId);
        if (item) {
          sortedItems.push(item);
        }
      });
      return sortedItems;
    }
    return null;
  }
}
@customElement('wm-franchiseeprojectview')
export class FranchiseeProjectView extends ProjectView {
  protected createView(): ProjectDataEntryView {
    return new FranchiseeProjectDEView(this.options, this);
  }
}
